import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { PieceRateService } from 'projects/warehousing/src/app/services/piece-rate.service';
import { PieceRateAdjSummary } from '@shared/models/piece-rate-summary';
import { DateTime } from 'luxon';
import { Subscription, combineLatest } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import {ActivityType, PieceRateActivity} from '@shared/models/piece-rate-activity';
import { CombinedShiftTotals } from '@shared/models/combined-shift-totals';

@Component({
    selector: 'app-piece-rate-summary',
    templateUrl: './piece-rate-summary.component.html',
    styleUrls: ['./piece-rate-summary.component.scss']
})
export class PieceRateSummaryComponent implements OnInit, OnDestroy {
    _activity: PieceRateActivity;

    @Input() set activity(act: PieceRateActivity) {
        this._activity = act;
    }

    get activity(): PieceRateActivity {
        return this._activity;
    }

    get inventoryString(): string {
        if (this._activity.activityType === ActivityType.ChepRepair)
            return `Total Calculated ${this._activity.description}`
        else
            return `Inventory ${this._activity.description}`;
    }

    get repairsByTempString(): string {
        return `${this._activity.description} By Temps`;
    }

    get averageString(): string {
        return [ActivityType.ChepRepair].includes(this._activity.activityType)
            ? 'Per Employee Adjustment'
            : 'Per Employee Variance';
    }

    get totalString(): string {
        return `Total ${this._activity.description}`;
    }

    averageNumber: number;
    totalNumber: number;
    invRepairs: number;
    builderRepairs: number;
    numberBuilders: number;
    weekIsClosed: boolean;
    inventoryForm: UntypedFormGroup = new UntypedFormGroup({
        inventoryNumber: new UntypedFormControl(),
        repairsByTempNumber: new UntypedFormControl()
    });
    componentsForm: UntypedFormGroup = new UntypedFormGroup({
        componentsNumber: new UntypedFormControl(),
        componentsPerRepairNumber: new UntypedFormControl({value: 0, disabled: true})
    });
    otherShiftsForm: UntypedFormGroup = new UntypedFormGroup({
        invRepairs: new UntypedFormControl({value: 0, disabled: true}),
        builderRepairs: new UntypedFormControl({value: 0, disabled: true}),
        numberBuilders: new UntypedFormControl({value: 0, disabled: true}),
    });

    pieceRateSummary: PieceRateAdjSummary = {};

    siteId: string;
    date: DateTime;
    shiftId: number;

    subscriptions: Subscription[];

    disabled = true;
    showComponentsFields: boolean;

    combinedShiftFlag: boolean;
    combinedTotals: CombinedShiftTotals;

    constructor(private pieceRateService: PieceRateService, private shiftContextService: ShiftContextService) { }

    ngOnInit(): void {
        this.subscriptions = [
            this.pieceRateService.CurrentPieceRateSummary.subscribe(summary => {
                this.pieceRateSummary = summary;

                this.inventoryForm.controls.inventoryNumber.setValue(this.pieceRateSummary.adjustmentTotal);
                this.inventoryForm.controls.repairsByTempNumber.setValue(this.pieceRateSummary.adjustmentTotalByTemps);
                this.componentsForm.controls.componentsNumber.setValue(this.pieceRateSummary.componentUsageTotal);
            }),
            this.pieceRateService.CurrentCombinedTotals.subscribe(totals => {
                this.combinedTotals = totals;

                this.otherShiftsForm.controls.invRepairs.setValue(this.combinedTotals?.inventoryTotal ?? 0);
                this.otherShiftsForm.controls.builderRepairs.setValue(this.combinedTotals?.builderTotal ?? 0);
                this.otherShiftsForm.controls.numberBuilders.setValue(this.combinedTotals?.builderCount ?? 0);
            }),
            this.pieceRateService.CurrentEnteredTotal.subscribe(tot => {
                this.totalNumber = tot;
            }),
            this.pieceRateService.CurrentAverage.subscribe(av => {
                this.averageNumber = av;
            }),
            combineLatest([
                this.componentsForm.get('componentsNumber').valueChanges,
                this.inventoryForm.get('inventoryNumber').valueChanges
            ]).subscribe(([components, inventory]) => {
                const cpr = inventory ? (+components/+inventory) : 0.0;
                this.componentsForm.controls.componentsPerRepairNumber.setValue(cpr.toFixed(1));
            }),
            combineLatest([
                this.pieceRateService.CurrentPieceRateAdjustment,
                this.pieceRateService.CurrentSiteWeek,
                this.shiftContextService.ImmediateSelection
            ]).subscribe(([buildersPieceRateAdjustment, siteWeek, context]) => {
                const combineShiftsConfig = siteWeek?.combinedShifts ?? false;

                this.combinedShiftFlag = combineShiftsConfig;

                if (buildersPieceRateAdjustment) {
                    const entry = buildersPieceRateAdjustment.activityConfigs
                        .find(ae => ae.pieceRateActivity === this._activity.id);

                    this.showComponentsFields = entry?.showComponentsFields ?? false;

                    this.weekIsClosed = buildersPieceRateAdjustment.weekIsClosed;

                    if (this.weekIsClosed || (combineShiftsConfig && context.shiftNum > 1)) {
                        this.componentsForm.controls.componentsNumber.disable();
                        this.inventoryForm.controls.inventoryNumber.disable();
                        this.inventoryForm.controls.repairsByTempNumber.disable();
                    } else {
                        this.componentsForm.controls.componentsNumber.enable();
                        this.inventoryForm.controls.inventoryNumber.enable();
                        this.inventoryForm.controls.repairsByTempNumber.enable();
                    }
                }
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    onChangeInventoryNumber(): void {
        const inventory = this.inventoryForm.value.inventoryNumber;
        const repairsByTemp = this.inventoryForm.value.repairsByTempNumber;

        this.pieceRateService.putPieceRateSummary({
            ...this.pieceRateSummary,
            adjustmentTotal: inventory,
            adjustmentTotalByTemps: repairsByTemp
        });
    }

    onChangeComponentsNumber(): void {
        const components = this.componentsForm.value.componentsNumber;

        this.pieceRateService.putPieceRateSummary({
            ...this.pieceRateSummary,
            componentUsageTotal: components
        });
    }
}
